import {
    changeCaseMembers,
    createCase,
    deleteCase,
    setWorkCase,
    updateCase,
} from '~/backend/connector-next-api/patientCaseClient';
import { usePatientWrite } from './usePatientWrite';

/** One change to a patient's cases. */
export type CaseCommand =
    | {
          type: 'create';
          name: string;
          datasetIds: readonly string[];
          /** Told the new case's id once the Connector has made it. */
          onCreated?: (caseId: string) => void;
      }
    | { type: 'rename'; caseId: string; name: string }
    | { type: 'archive'; caseId: string; archived: boolean }
    | {
          type: 'members';
          caseId: string;
          add?: readonly string[];
          remove?: readonly string[];
      }
    | { type: 'delete'; caseId: string }
    | { type: 'workCase'; caseId: string | null };

/** Sends one case change; resolves to the patient's next version. */
async function sendCaseCommand(
    command: CaseCommand,
    patientId: string,
    version: string
): Promise<string> {
    switch (command.type) {
        case 'create': {
            const created = await createCase(
                patientId,
                command.name,
                command.datasetIds,
                version
            );
            command.onCreated?.(created.id);
            return created.version;
        }
        case 'rename':
            return updateCase(
                patientId,
                command.caseId,
                { name: command.name },
                version
            );
        case 'archive':
            return updateCase(
                patientId,
                command.caseId,
                { archived: command.archived },
                version
            );
        case 'members':
            return changeCaseMembers(
                patientId,
                command.caseId,
                { add: command.add, remove: command.remove },
                version
            );
        case 'delete':
            return deleteCase(patientId, command.caseId, version);
        case 'workCase':
            return setWorkCase(patientId, command.caseId, version);
    }
}

/**
 * Changes a patient's cases as patient writes: each guarded by the patient's
 * version, reporting saving while any write to the patient is in flight.
 */
export function usePatientCases(patient: {
    id?: string | null;
    version?: string | null;
}) {
    return usePatientWrite(patient, sendCaseCommand);
}
